import React, { useState, useEffect, useRef } from "react";
import { Plus, Trash2, MessageSquareText, CalendarClock, Sparkles } from "lucide-react";

const QUICK_PICKS = [
  { label: "+3d", days: 3 },
  { label: "+1w", days: 7 },
  { label: "+2w", days: 14 },
  { label: "+1m", days: 30 },
];

const MAX_BODY_LENGTH = 1000;
let localKeySeq = 0;
const nextLocalKey = () => `local-${++localKeySeq}`;

function toISODate(d) {
  return d.toISOString().slice(0, 10);
}

function addDaysISO(baseISO, days) {
  const base = baseISO ? new Date(`${baseISO}T00:00:00`) : new Date();
  const next = new Date(base.getTime());
  next.setDate(next.getDate() + days);
  return toISODate(next);
}

// Full literal class strings per accent, chosen at runtime by object key -
// NOT built via template interpolation (e.g. `bg-${accent}-50`), since
// Tailwind's JIT scanner only picks up complete class-name tokens that
// appear verbatim in the source text; an interpolated fragment would never
// match and the color would silently fail to render.
const THEMES = {
  blue: {
    cardHover: "hover:border-blue-200",
    suggestion: "text-blue-700 hover:text-blue-900 bg-blue-50 hover:bg-blue-100",
    focusRing: "focus:ring-blue-500 focus:border-blue-500",
    quickPickActive: "bg-blue-200 text-blue-900",
    quickPickInactive: "bg-blue-100 text-blue-800 hover:bg-blue-200",
  },
  amber: {
    cardHover: "hover:border-amber-200",
    suggestion: "text-amber-700 hover:text-amber-900 bg-amber-50 hover:bg-amber-100",
    focusRing: "focus:ring-amber-500 focus:border-amber-500",
    quickPickActive: "bg-amber-200 text-amber-900",
    quickPickInactive: "bg-amber-100 text-amber-800 hover:bg-amber-200",
  },
};

// One shared editable row, used inside both sections below - the only
// difference between a "patient reminder" row and a "booking reminder" row
// is the fixed `kind` of the section it lives in, so there's no Type select
// here anymore (removed per feedback: mixing both kinds in one list with a
// per-row dropdown didn't give either type its own visual space).
function FollowUpRow({ row, onChange, onRemove, disabled, sessionDate, today, bodyLabel, bodyPlaceholder, accent, suggestion, bodyRef }) {
  const theme = THEMES[accent];
  return (
    <div className={`border border-gray-200 rounded-lg p-3 space-y-2 ${theme.cardHover} transition-colors`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center justify-between gap-2 flex-1 min-w-0">
          <label className="text-[11px] font-semibold text-gray-600">{bodyLabel}</label>
          {suggestion && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => onChange("body", suggestion)}
              className={`inline-flex items-center gap-1 text-[10.5px] font-semibold ${theme.suggestion} px-2 py-0.5 rounded transition-colors cursor-pointer shrink-0`}
              title="Fill with a message based on the Plan / Advice section"
            >
              <Sparkles className="w-3 h-3" />
              Use advice message
            </button>
          )}
        </div>
      </div>
      <textarea
        ref={bodyRef}
        value={row.body}
        disabled={disabled}
        onChange={(e) => onChange("body", e.target.value)}
        placeholder={bodyPlaceholder}
        rows={2}
        maxLength={MAX_BODY_LENGTH}
        className={`w-full text-xs text-gray-900 border border-gray-300 rounded-md px-2.5 py-2 focus:outline-none focus:ring-1 ${theme.focusRing} bg-white placeholder:text-gray-400 transition-colors resize-y`}
      />
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-semibold text-gray-600 shrink-0">When:</span>
        {QUICK_PICKS.map((qp) => {
          const computed = addDaysISO(sessionDate, qp.days);
          const active = row.trigger_date === computed;
          return (
            <button
              key={qp.label}
              type="button"
              disabled={disabled}
              onClick={() => onChange("trigger_date", computed)}
              className={`text-[10.5px] font-medium px-2 py-0.5 rounded transition-colors cursor-pointer shrink-0 ${
                active ? theme.quickPickActive : theme.quickPickInactive
              }`}
            >
              {qp.label}
            </button>
          );
        })}
        <input
          type="date"
          min={today}
          value={row.trigger_date}
          disabled={disabled}
          onChange={(e) => onChange("trigger_date", e.target.value)}
          className={`h-7 text-[11px] text-gray-800 border border-gray-300 rounded-md px-2 py-0.5 focus:outline-none focus:ring-1 ${theme.focusRing} bg-white shrink-0`}
        />
        <button
          type="button"
          disabled={disabled}
          onClick={onRemove}
          className="p-1 ml-auto text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer shrink-0"
          title="Remove"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function HistoryRow({ item, accent }) {
  return (
    <div className={`flex items-center gap-2 text-[11px] px-2.5 py-1.5 rounded-lg bg-gray-50 border border-gray-200 text-gray-500`}>
      <span className="flex-1 truncate">{item.body}</span>
      <span className="shrink-0 font-semibold">{item.trigger_date}</span>
      <span
        className={`shrink-0 px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wide ${
          item.status === "sent" || item.status === "done" ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-600"
        }`}
      >
        {item.status}
      </span>
    </div>
  );
}

// Cloned from TestsTable.js's row-editor architecture (parse-guard via a
// ref, add/remove row, Tab-to-add-button focus flow), emitting a plain JS
// array directly rather than a pipe-delimited string, since the backend is
// a real table (ConsultationFollowUpModel), not a gp_*-style Text blob.
// Renders two fully separate sections - Patient Reminders and Booking
// Reminders - so each gets its own dedicated space rather than being mixed
// into one list with a per-row type picker.
export default function FollowUpTable({
  value,
  onChange,
  disabled = false,
  sessionDate,
  hasAppointment = false,
  suggestedPatientMessage = "",
  name = "follow_up_items",
}) {
  const [rows, setRows] = useState([]);
  const lastEmittedRef = useRef(null);
  const newPatientRowBodyRef = useRef(null);
  const newTaskRowBodyRef = useRef(null);
  const focusTargetRef = useRef(null); // "patient_msg" | "doctor_task" | null

  const allItems = Array.isArray(value) ? value : [];
  const historyItems = allItems.filter((it) => it.status && it.status !== "pending");
  const patientHistory = historyItems.filter((it) => it.kind === "patient_msg");
  const taskHistory = historyItems.filter((it) => it.kind === "doctor_task");
  const cleanSuggestion = (suggestedPatientMessage || "").trim().slice(0, MAX_BODY_LENGTH);

  useEffect(() => {
    const incomingPending = allItems.filter((it) => !it.status || it.status === "pending");
    if (incomingPending === lastEmittedRef.current) return;
    if (JSON.stringify(incomingPending) === JSON.stringify(lastEmittedRef.current)) return;
    setRows(incomingPending.map((it) => ({
      _lk: it.id ? `id-${it.id}` : nextLocalKey(),
      kind: it.kind || "doctor_task",
      body: it.body || "",
      trigger_date: it.trigger_date || "",
    })));
    // Intentionally keyed only on `value` - `allItems` is derived from it each
    // render, and including it would re-trigger this on every parent render.
  }, [value]); // eslint-disable-line

  useEffect(() => {
    if (focusTargetRef.current === "patient_msg") {
      newPatientRowBodyRef.current?.focus();
    } else if (focusTargetRef.current === "doctor_task") {
      newTaskRowBodyRef.current?.focus();
    }
    focusTargetRef.current = null;
  }, [rows.length]);

  const syncToParent = (updatedRows) => {
    setRows(updatedRows);
    const cleaned = updatedRows
      .filter((r) => r.body.trim() !== "" || r.trigger_date)
      .map(({ kind, body, trigger_date }) => ({ kind, body, trigger_date }));
    lastEmittedRef.current = cleaned;
    onChange({ target: { name, value: cleaned } });
  };

  const handleRowChange = (localKey, field, val) => {
    syncToParent(rows.map((r) => (r._lk === localKey ? { ...r, [field]: val } : r)));
  };

  const handleRemoveRow = (localKey) => {
    syncToParent(rows.filter((r) => r._lk !== localKey));
  };

  const handleAddRow = (kind) => {
    focusTargetRef.current = kind;
    const body = kind === "patient_msg" && cleanSuggestion ? cleanSuggestion : "";
    syncToParent([...rows, { _lk: nextLocalKey(), kind, body, trigger_date: "" }]);
  };

  const today = toISODate(new Date());
  const patientRows = rows.filter((r) => r.kind === "patient_msg");
  const taskRows = rows.filter((r) => r.kind === "doctor_task");

  return (
    <div className="space-y-4">
      {/* ===== Patient Reminders ===== */}
      <div className="bg-white border border-gray-200 rounded-xl p-3 sm:p-4 shadow-2xs">
        <div className="flex items-center gap-2.5 border-b border-gray-100 pb-2.5 mb-3">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-50 border border-blue-200 text-blue-600 shrink-0 select-none">
            <MessageSquareText className="w-4 h-4" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900 leading-none">Patient Reminders</h3>
            <p className="text-[11px] text-gray-500 mt-0.5 leading-tight">
              Scheduled messages sent automatically to the patient in-app on the date you pick.
            </p>
          </div>
        </div>

        {!hasAppointment && (
          <p className="text-[11px] text-amber-700 mb-2.5">No appointment linked yet - patient reminders need a real appointment to deliver to.</p>
        )}

        {patientHistory.length > 0 && (
          <div className="mb-2.5 space-y-1.5">
            {patientHistory.map((h) => <HistoryRow key={h.id} item={h} accent="blue" />)}
          </div>
        )}

        {patientRows.length === 0 ? (
          <p className="text-[11px] text-gray-400 italic mb-3">No patient reminders scheduled.</p>
        ) : (
          <div className="space-y-3 mb-3">
            {patientRows.map((row, idx) => (
              <FollowUpRow
                key={row._lk}
                row={row}
                disabled={disabled || !hasAppointment}
                sessionDate={sessionDate}
                today={today}
                accent="blue"
                bodyLabel="Message to Patient"
                bodyPlaceholder="e.g. How is the breathing exercise going? Keep it up!"
                suggestion={cleanSuggestion}
                bodyRef={idx === patientRows.length - 1 ? newPatientRowBodyRef : null}
                onChange={(field, val) => handleRowChange(row._lk, field, val)}
                onRemove={() => handleRemoveRow(row._lk)}
              />
            ))}
          </div>
        )}

        <button
          type="button"
          disabled={disabled || !hasAppointment}
          onClick={() => handleAddRow("patient_msg")}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded-lg font-semibold text-xs transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-blue-100"
        >
          <Plus className="w-3.5 h-3.5 text-blue-600" />
          Add Patient Reminder
        </button>
      </div>

      {/* ===== Booking / Follow-Up Reminders (for the doctor) ===== */}
      <div className="bg-white border border-gray-200 rounded-xl p-3 sm:p-4 shadow-2xs">
        <div className="flex items-center gap-2.5 border-b border-gray-100 pb-2.5 mb-3">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-50 border border-amber-200 text-amber-600 shrink-0 select-none">
            <CalendarClock className="w-4 h-4" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900 leading-none">Booking Reminders</h3>
            <p className="text-[11px] text-gray-500 mt-0.5 leading-tight">
              A nudge for you (and staff) to re-engage this patient and book their next visit - never auto-booked.
            </p>
          </div>
        </div>

        {taskHistory.length > 0 && (
          <div className="mb-2.5 space-y-1.5">
            {taskHistory.map((h) => <HistoryRow key={h.id} item={h} accent="amber" />)}
          </div>
        )}

        {taskRows.length === 0 ? (
          <p className="text-[11px] text-gray-400 italic mb-3">No booking reminders scheduled.</p>
        ) : (
          <div className="space-y-3 mb-3">
            {taskRows.map((row, idx) => (
              <FollowUpRow
                key={row._lk}
                row={row}
                disabled={disabled}
                sessionDate={sessionDate}
                today={today}
                accent="amber"
                bodyLabel="Note to Self"
                bodyPlaceholder="e.g. Check in on mood trend"
                suggestion={null}
                bodyRef={idx === taskRows.length - 1 ? newTaskRowBodyRef : null}
                onChange={(field, val) => handleRowChange(row._lk, field, val)}
                onRemove={() => handleRemoveRow(row._lk)}
              />
            ))}
          </div>
        )}

        <button
          type="button"
          disabled={disabled}
          onClick={() => handleAddRow("doctor_task")}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 rounded-lg font-semibold text-xs transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-amber-500 focus:bg-amber-100"
        >
          <Plus className="w-3.5 h-3.5 text-amber-600" />
          Add Booking Reminder
        </button>
      </div>
    </div>
  );
}
